/**
 * PROJECTION (end-to-end capability of 🎯 F0b) — PURE logic.
 *
 * Ordering of projectable items, splitting content into slides and presenter
 * navigation. No DOM or framework: the web projection view (and in F2 the
 * P2P-LAN output) consume these helpers as-is.
 */

export type ProjectionKind = "song" | "verse" | "theme" | "announcement";

/** `projection_item` row as it arrives from local SQLite. */
export interface ProjectionItemRow {
  id: string;
  church_id: string | null;
  venue_id: string | null;
  kind: string | null;
  title: string | null;
  content: string | null;
  /** PRESENTER notes (F2): never go out over the live protocol. */
  notes?: string | null;
  sort_order: number | null;
  deleted_at: string | null;
}

/** Visible, sorted items: out of trash; manual order, then title. */
export function sortProjectionItems<T extends ProjectionItemRow>(rows: readonly T[]): T[] {
  return rows
    .filter((r) => !r.deleted_at)
    .toSorted((a, b) => {
      const orderA = a.sort_order ?? 0;
      const orderB = b.sort_order ?? 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a.title ?? "").localeCompare(b.title ?? "");
    });
}

/**
 * Splits an item's content into SLIDES: blocks separated by a blank line
 * (song stanzas, verses, topic points). No content → a single slide with
 * the title.
 */
export function splitSlides(item: Pick<ProjectionItemRow, "title" | "content">): string[] {
  const content = item.content?.trim();
  if (!content) {
    return [item.title?.trim() ?? ""];
  }
  return content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
}

/** Pure presenter state: which item and which slide are live. */
export interface PresenterState {
  itemIndex: number;
  slideIndex: number;
}

export const INITIAL_PRESENTER_STATE: PresenterState = {
  itemIndex: 0,
  slideIndex: 0,
};

/**
 * Advances one slide; when the item is exhausted, moves to the next. Returns the
 * same state at the end (no wrap: the operator sees it finished).
 */
export function nextSlide(state: PresenterState, slideCounts: readonly number[]): PresenterState {
  const count = slideCounts[state.itemIndex] ?? 0;
  if (state.slideIndex + 1 < count) {
    return { itemIndex: state.itemIndex, slideIndex: state.slideIndex + 1 };
  }
  if (state.itemIndex + 1 < slideCounts.length) {
    return { itemIndex: state.itemIndex + 1, slideIndex: 0 };
  }
  return state;
}

/** Goes back one slide; at an item's start, jumps to the previous item's end. */
export function previousSlide(
  state: PresenterState,
  slideCounts: readonly number[],
): PresenterState {
  if (state.slideIndex > 0) {
    return { itemIndex: state.itemIndex, slideIndex: state.slideIndex - 1 };
  }
  if (state.itemIndex > 0) {
    const prevCount = slideCounts[state.itemIndex - 1] ?? 1;
    return {
      itemIndex: state.itemIndex - 1,
      slideIndex: Math.max(0, prevCount - 1),
    };
  }
  return state;
}

/**
 * Jumps directly to an item/slide (presenter "go to"). Indices are
 * CLAMPED to what exists: never produces an out-of-range state.
 */
export function goToSlide(
  itemIndex: number,
  slideIndex: number,
  slideCounts: readonly number[],
): PresenterState {
  if (slideCounts.length === 0) {
    return INITIAL_PRESENTER_STATE;
  }
  const item = Math.min(Math.max(0, itemIndex), slideCounts.length - 1);
  const count = slideCounts[item] ?? 1;
  const slide = Math.min(Math.max(0, slideIndex), Math.max(0, count - 1));
  return { itemIndex: item, slideIndex: slide };
}

/** Normalizes text for tolerant search (lowercase + no accents). */
function searchable(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/[̀-ͯ]/gu, "");
}

/**
 * Filters projectable items by text (title/content/type), case- and
 * accent-tolerant. Empty search → all items.
 */
export function filterProjectionItems<T extends ProjectionItemRow>(
  rows: readonly T[],
  search: string,
): T[] {
  const query = searchable(search).trim();
  if (!query) {
    return [...rows];
  }
  return rows.filter((row) =>
    [row.title, row.content, row.kind].some((field) => searchable(field).includes(query)),
  );
}
